import axiosInstance from "./AxiosInstance"

const API_URL = "/trainerdashboard"
const TRAINER_CRUD_API = "/trainers"

// Request cache and in-progress requests tracking
const requestCache = new Map()
const inProgressRequests = new Map()

// Helper function to deduplicate requests
const deduplicatedRequest = async (key, requestFn) => {
  if (inProgressRequests.has(key)) return inProgressRequests.get(key)
  if (requestCache.has(key)) return requestCache.get(key)

  const requestPromise = requestFn().then(result => {
    // Only cache if we want to, for now let's just use it for in-progress deduplication
    // requestCache.set(key, result) 
    inProgressRequests.delete(key)
    return result
  }).catch(error => {
    inProgressRequests.delete(key)
    throw error
  })

  inProgressRequests.set(key, requestPromise)
  return requestPromise
}

export const clearTrainerCache = () => {
  requestCache.clear()
  inProgressRequests.clear()
}

export const getTrainerById = async (trainerId) => {
  return (await axiosInstance.get(`${TRAINER_CRUD_API}/${trainerId}`)).data
}

export const getTrainerByTrainerId = async (trainerId) => {
  return (await axiosInstance.get(`${TRAINER_CRUD_API}/trainer/${trainerId}`)).data
}

export const getTrainerMetrics = async (trainerId) => {
  const key = `metrics-${trainerId}`
  return deduplicatedRequest(key, async () => {
    const response = await axiosInstance.get(`${API_URL}/metrics`, { 
      params: { trainerId }
    })
    return response.data.data
  })
}

/**
 * Richer companion to getTrainerMetrics: teaching load, attendance rate,
 * course mix, and a blended performance band. Returns null rather than
 * throwing — the dashboard must still render if this one call fails.
 */
export const getTrainerInsights = async (trainerId) => {
  const key = `insights-${trainerId}`
  return deduplicatedRequest(key, async () => {
    try {
      const response = await axiosInstance.get(`${API_URL}/insights`, {
        params: { trainerId }
      })
      return response.data.data
    } catch (error) {
      console.error("Error fetching trainer insights", error)
      return null
    }
  })
}

export const getBatchesByTrainer = async (trainerId, params = {}) => {
  const response = await axiosInstance.get(`${API_URL}/mybatches`, { 
    params: { ...params, trainerId }
  })
  const resData = response.data.data;
  return {
    data: resData.activeBatches.concat(resData.upcomingBatches).concat(resData.completedBatches),
    totalCount: resData.totalBatches || 0,
    activeBatches: resData.activeBatches || [],
    completedBatches: resData.completedBatches || [],
    upcomingBatches: resData.upcomingBatches || [],
    // Per-state counts of what matched, the per-state roll-ups and the
    // dropdown options -- all worked out by the API.
    counts: {
      active: resData.totalActiveBatches || 0,
      upcoming: resData.totalUpcomingBatches || 0,
      completed: resData.totalCompletedBatches || 0,
    },
    summary: resData.summary || null,
    filterOptions: resData.filterOptions || null,
  }
}

// The Mock Interviews tab: rows for one page, plus the All / Upcoming / Past
// counts over the whole match. `when` narrows the rows, never the counts.
export const getTrainerMockInterviews = async (trainerId, params = {}) => {
  const clean = Object.fromEntries(
    Object.entries({ ...params, trainerId }).filter(([, v]) => v !== undefined && v !== null && v !== "")
  )
  const response = await axiosInstance.get(`${API_URL}/mock-interviews`, { params: clean })
  return {
    data: Array.isArray(response.data?.data) ? response.data.data : [],
    pagination: response.data?.pagination || {},
    counts: response.data?.meta?.counts || { all: 0, upcoming: 0, past: 0 },
  }
}

export const getStudentsByTrainer = async (trainerId, params = {}) => {
  const { page = 1, limit = 10, search = "", batchId = null } = params
  const response = await axiosInstance.get(`${API_URL}/mystudents`, { 
    params: { 
      page, 
      limit, 
      search, 
      batchId, 
      trainerId 
    }
  })
  return {
    data: response.data.data || [],
    totalCount: response.data.pagination?.total || 0,
    pagination: response.data.pagination
  }
}

export const getClassroomsByTrainer = async (trainerId, params = {}) => {
  const response = await axiosInstance.get(`${API_URL}/myclassrooms`, { 
    params: { ...params, trainerId }
  })
  return {
    data: response.data.data || [],
    totalCount: response.data.pagination?.total || 0,
    pagination: response.data.pagination
  }
}

export const getTrainerDashboardData = async (trainerId) => {
  const key = `full-dashboard-${trainerId}`
  return deduplicatedRequest(key, async () => {
    const [trainerResp, metrics, batchesData, insights] = await Promise.all([
      getTrainerByTrainerId(trainerId),
      getTrainerMetrics(trainerId),
      getTrainerAllBatchesWithEvents(trainerId),
      getTrainerInsights(trainerId)
    ])

    return {
      trainer: trainerResp.data,
      stats: metrics,
      insights,
      batches: batchesData.activeBatches.concat(batchesData.upcomingBatches).concat(batchesData.completedBatches),
      activeBatches: batchesData.activeBatches,
      completedBatches: batchesData.completedBatches,
      upcomingBatches: batchesData.upcomingBatches,
      batchSummary: batchesData.summary || null,
      batchFilterOptions: batchesData.filterOptions || null,
      batchCounts: {
        active: batchesData.totalActiveBatches || 0,
        upcoming: batchesData.totalUpcomingBatches || 0,
        completed: batchesData.totalCompletedBatches || 0,
      },
      activeEvents: batchesData.activeEvents || [],
      totalEvents: batchesData.totalEvents || 0,
      classrooms: [], // Fetch on demand
      students: []
    }
  })
}

export const getTrainerTodaySchedule = async (trainerId) => {
  const response = await axiosInstance.get(`${API_URL}/myclassrooms`, {
    params: { trainerId, limit: 100 }
  })
  const today = new Date().toISOString().split("T")[0]
  const todaySchedule = (response.data.data.items || []).filter(cls => {
    if (cls.date) {
      return new Date(cls.date).toISOString().split("T")[0] === today
    }
    // Also check active batches if they have classes today
    return false
  })

  return {
    data: todaySchedule,
    totalCount: todaySchedule.length
  }
}

export const updateTrainerProfile = async (trainerId, trainerData) => {
  const response = await axiosInstance.put(`${TRAINER_CRUD_API}/${trainerId}`, trainerData)
  clearTrainerCache()
  return response.data
}

export const getTrainerAllBatchesWithEvents = async (trainerId) => {
  const response = await axiosInstance.get(`${API_URL}/batches`, { 
    params: { trainerId }
  })
  return response.data.data
}

export const getStudentAttendance = async (params = {}) => {
  const key = `attn-${params.studentId}-${params.batchId}`
  return deduplicatedRequest(key, async () => {
    const response = await axiosInstance.get(`${API_URL}/mystudentattendance`, { 
      params,
      timeout: 30000 
    })
    return response.data
  })
}

export const getStudentQuizHistory = async (params = {}) => {
  const key = `quiz-${params.studentId}-${params.batchId}`
  return deduplicatedRequest(key, async () => {
    const response = await axiosInstance.get(`${API_URL}/mystudentquizes`, { 
      params,
      timeout: 30000 
    })
    return response.data
  })
}

// Roster-level attendance: one row per student per batch, already filtered,
// sorted and paged by the API. Every filter is a query param so a view can be
// shared as a URL later without reworking this.
export const getAttendanceRoster = async (params = {}) => {
  const clean = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "")
  )
  const response = await axiosInstance.get(`${API_URL}/attendance-roster`, { params: clean })
  const payload = response.data?.data || {}
  return {
    rows: Array.isArray(payload.rows) ? payload.rows : [],
    summary: payload.summary || null,
    filterOptions: payload.filterOptions || { batches: [], courses: [], modes: [] },
    pagination: response.data?.pagination || null,
  }
}

export default {
  getAttendanceRoster,
  getTrainerMockInterviews,
  getTrainerById,
  getTrainerByTrainerId,
  getTrainerMetrics,
  getTrainerInsights,
  getBatchesByTrainer,
  getStudentsByTrainer,
  getClassroomsByTrainer,
  getTrainerDashboardData,
  getTrainerTodaySchedule,
  updateTrainerProfile,
  getTrainerAllBatchesWithEvents,
  getStudentAttendance,
  getStudentQuizHistory,
  clearTrainerCache,
}
